import {
  CheckIcon,
  CopyIcon,
  GiftIcon,
  LockSimpleIcon,
  ShareNetworkIcon,
  TicketIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useEffect, useState } from "react";

type PromoCodeCardProps = {
  isEligible: boolean;
  promoCode: string | null;
};

export const PromoCodeCard = ({ isEligible, promoCode }: PromoCodeCardProps) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timer = window.setTimeout(() => {
      setCopied(false);
    }, 2200);

    return () => {
      window.clearTimeout(timer);
    };
  }, [copied]);

  const copyPromoCode = async () => {
    if (!promoCode) {
      return;
    }

    await navigator.clipboard.writeText(promoCode);

    setCopied(true);
  };

  const sharePromoCode = async () => {
    if (!promoCode) {
      return;
    }

    const message = `Profitez de Mon CV Pro avec mon code promo ${promoCode}.`;

    if (navigator.share) {
      await navigator.share({
        title: "Code promo Mon CV Pro",
        text: message,
        url: window.location.origin,
      });

      return;
    }

    await navigator.clipboard.writeText(message);

    setCopied(true);
  };

  return (
    <article
      className={cn(
        "grid gap-4 rounded-2xl border border-slate-200",
        "bg-white p-4 shadow-sm",
        "lg:grid-cols-[minmax(250px,0.85fr)_minmax(0,1.25fr)]",
        "lg:items-center lg:px-5",
      )}
    >
      <div className="flex items-center gap-3.5">
        <span
          className={cn(
            "relative grid size-14 shrink-0 place-items-center",
            "rounded-2xl bg-gradient-to-br",
            "from-emerald-50 to-violet-100",
            "text-indigo-600",
          )}
        >
          <TicketIcon aria-hidden size={30} weight="duotone" />

          {isEligible ? (
            <GiftIcon
              aria-hidden
              size={18}
              weight="fill"
              className="absolute -bottom-1 -right-1 text-emerald-500"
            />
          ) : (
            <span className="absolute -bottom-1 -right-1 grid size-6 place-items-center rounded-full bg-slate-100 text-slate-500 shadow-sm">
              <LockSimpleIcon aria-hidden size={13} weight="fill" />
            </span>
          )}
        </span>

        <div className="min-w-0">
          <h2 className="text-sm font-bold text-indigo-950">Votre code promo</h2>

          <p className="mt-1 text-[11px] leading-4 text-slate-600 sm:text-xs">
            {isEligible
              ? "Partagez ce code pour faire profiter vos proches d’une réduction."
              : "Activez une offre SMART pour générer et partager votre code personnel."}
          </p>
        </div>
      </div>

      <div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
        <div
          className={cn(
            "flex min-h-11 min-w-0 items-center justify-center",
            "rounded-xl border px-3",
            promoCode
              ? "border-slate-200 bg-slate-50 text-indigo-950"
              : "border-slate-200 bg-slate-50 text-slate-400",
          )}
        >
          <span
            className={cn(
              "truncate text-sm font-bold tracking-wide",
              !promoCode && "text-xs font-medium tracking-normal",
            )}
          >
            {promoCode ?? "Code indisponible"}
          </span>
        </div>

        <button
          type="button"
          disabled={!promoCode}
          onClick={() => {
            void copyPromoCode();
          }}
          className={cn(
            "flex min-h-11 items-center justify-center gap-2",
            "rounded-xl border px-4 text-xs font-semibold",
            "transition-colors",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500",
            promoCode
              ? "border-indigo-600 bg-white text-indigo-700 hover:bg-indigo-50"
              : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400",
          )}
        >
          {copied ? (
            <CheckIcon aria-hidden size={17} weight="bold" />
          ) : (
            <CopyIcon aria-hidden size={17} />
          )}

          {copied ? "Copié" : "Copier"}
        </button>

        <button
          type="button"
          disabled={!promoCode}
          onClick={() => {
            void sharePromoCode();
          }}
          className={cn(
            "flex min-h-11 items-center justify-center gap-2",
            "rounded-xl px-4 text-xs font-semibold",
            "transition-colors",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500",
            promoCode
              ? "bg-indigo-700 text-white hover:bg-indigo-800"
              : "cursor-not-allowed bg-slate-100 text-slate-400",
          )}
        >
          <ShareNetworkIcon aria-hidden size={17} />
          Partager
        </button>
      </div>
    </article>
  );
};
